import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Avatar,
  Chip,
  Typography,
  Button,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  InputLabel,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountCircle as AccountCircleIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  MiscellaneousServices as ServicesIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatarCargo, getCargoColor, compareValues } from '../../utils/formatters';
import ServicosRapidosForm from './ServicosRapidosForm';
import useServicos from '../../hooks/useServicos';
import { toast } from 'react-toastify';

/**
 * Componente de cabeçalho de coluna ordenável
 * @param {Object} props - Propriedades do componente
 * @param {string} props.column - Identificador da coluna
 * @param {string} props.label - Texto a ser exibido
 * @param {string} props.orderBy - Coluna atual de ordenação
 * @param {string} props.orderDirection - Direção atual da ordenação
 * @param {Function} props.onSort - Função para alterar ordenação
 * @returns {JSX.Element}
 */
const SortableTableCell = ({ column, label, orderBy, orderDirection, onSort }) => {
  const tooltipText = orderBy === column
    ? `Classificado por ${label} em ordem ${orderDirection === 'asc' ? 'crescente' : 'decrescente'}`
    : `Clique para classificar por ${label}`;

  return (
    <TableCell
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme => alpha(theme.palette.primary.main, 0.04)
        },
        backgroundColor: orderBy === column ? theme => alpha(theme.palette.primary.main, 0.08) : 'inherit',
        fontWeight: 'bold',
        color: orderBy === column ? 'primary.main' : 'inherit',
      }}
      onClick={() => onSort(column)}
    >
      <Tooltip title={tooltipText} placement="top" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {label}
          {orderBy === column ? (
            <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
              {orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            </Box>
          ) : null}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

/**
 * Componente de tabela de usuários
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.usuarios - Lista de usuários
 * @param {string} props.searchTerm - Termo de busca
 * @param {Function} props.onSearchChange - Função para alterar o termo de busca
 * @param {Function} props.onClearSearch - Função para limpar a busca
 * @param {Function} props.onEdit - Função para editar um usuário
 * @param {Function} props.onDelete - Função para excluir um usuário
 * @param {Function} props.getImageUrl - Função para obter a URL da imagem
 * @param {Function} props.handleImageError - Função para tratar erros de imagem
 * @param {Object} props.customTheme - Tema personalizado
 * @param {Function} props.atualizarGuicheUsuario - Função para atualizar o guichê de um usuário
 * @param {Function} props.atualizarServicosUsuario - Função para atualizar os serviços de um usuário
 * @returns {JSX.Element}
 */
const UsuarioTable = ({
  usuarios,
  searchTerm,
  onSearchChange,
  onClearSearch,
  onEdit,
  onDelete,
  getImageUrl,
  handleImageError,
  customTheme,
  atualizarGuicheUsuario,
  atualizarServicosUsuario
}) => {
  const [orderBy, setOrderBy] = React.useState('nome');
  const [orderDirection, setOrderDirection] = React.useState('asc');
  // Estados para o menu de configurações rápidas
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedUsuario, setSelectedUsuario] = React.useState(null);
  const [tempGuiche, setTempGuiche] = React.useState('');
  const [editedServicos, setEditedServicos] = React.useState({
    atende: [],
    prioridades: {}
  });
  const [searchServico, setSearchServico] = React.useState('');
  const [alteracoesFeitas, setAlteracoesFeitas] = React.useState(false);
  
  // Estado para controlar o diálogo de confirmação de desativação
  const [confirmDesativarOpen, setConfirmDesativarOpen] = React.useState(false);
  const [usuarioToDesativar, setUsuarioToDesativar] = React.useState(null);

  // Obter serviços do hook
  const {
    servicosDisponiveis,
    carregando: carregandoServicos,
    erro: erroServicos,
    carregarServicos,
    formatarServicosAtendidosParaSalvar,
    formatarServicosAtendidosParaExibir
  } = useServicos();

  // Função para lidar com a alteração na ordenação
  const handleSort = (column) => {
    if (orderBy === column) {
      // Se já estamos ordenando por esta coluna, alternar a direção
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Caso contrário, definir a nova coluna e resetar a direção para 'asc'
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  // Filtrar usuários com base no termo de busca
  const filteredUsuarios = React.useMemo(() => {
    if (!searchTerm) return usuarios;
    
    const term = searchTerm.toLowerCase().trim();
    return usuarios.filter(usuario => (
      usuario.usuario.toLowerCase().includes(term) ||
      usuario.nome.toLowerCase().includes(term) ||
      usuario.email.toLowerCase().includes(term) ||
      formatarCargo(usuario.cargo).toLowerCase().includes(term)
    ));
  }, [usuarios, searchTerm]);

  // Filtrar e ordenar os usuários
  const sortedAndFilteredUsuarios = React.useMemo(() => {
    return filteredUsuarios
      .slice() // Criar uma cópia para não modificar o original
      .sort((a, b) => {
        const compare = compareValues(a, b, orderBy);
        // Inverter resultado se a ordenação for decrescente
        return orderDirection === 'asc' ? compare : -compare;
      });
  }, [filteredUsuarios, orderBy, orderDirection]);

  // Componente auxiliar para Avatar com melhor tratamento de erros
  const UserAvatar = ({ usuario }) => {
    const [imgSrc, setImgSrc] = React.useState(() => {
      try {
        // Gerar URL inicial da imagem
        let src = null;
        
        if (usuario.imagem_perfil) {
          console.log('Caminho da imagem original:', usuario.imagem_perfil);
          src = getImageUrl(usuario.imagem_perfil);
          console.log('URL da imagem gerada:', src);
        } else {
          // Se não houver imagem, usar imagem padrão
          const { getDefaultImage } = require('../../utils/formatters');
          src = getDefaultImage();
        }
        
        return src;
      } catch (error) {
        console.error('Erro ao gerar URL da imagem:', error);
        // Retornar imagem padrão em caso de erro
        const { getDefaultImage } = require('../../utils/formatters');
        return getDefaultImage();
      }
    });
    
    // Função dedicada para tratamento de erros de imagem
    const handleLocalImageError = (e) => {
      console.log('Erro de carregamento de imagem no Avatar:', e.target.src);
      
      // Tentar URLs alternativas
      if (handleImageError) {
        handleImageError(e, customTheme);
      } else {
        const { getDefaultImage } = require('../../utils/formatters');
        setImgSrc(getDefaultImage());
        // Impedir mais tentativas
        e.target.onerror = null;
      }
    };
    
    const isInativo = usuario && usuario.ativo === false;
    
    return (
      <Avatar
        src={imgSrc}
        alt={usuario.nome}
        sx={{ 
          width: 36, 
          height: 36,
          border: '1px solid',
          borderColor: isInativo ? 'rgba(0,0,0,0.2)' : 'divider',
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? alpha(customTheme.palette.primary.dark, 0.2)
            : alpha(customTheme.palette.primary.light, 0.2),
          ...(isInativo && {
            filter: 'grayscale(100%)',
            opacity: 0.8
          }),
          '& .MuiAvatar-img': {
            objectFit: 'cover',
            width: '100%',
            height: '100%'
          }
        }}
        imgProps={{
          onError: handleLocalImageError
        }}
      >
        <AccountCircleIcon />
      </Avatar>
    );
  };

  // Função para abrir o menu de configurações
  const handleSettingsClick = (event, usuario) => {
    setAnchorEl(event.currentTarget);
    setSelectedUsuario(usuario);
    setTempGuiche(usuario.guiche || '');
    
    // Inicializar serviços atendidos
    if (usuario.servicos_atendidos) {
      // Converter prioridades numéricas para formato textual
      const servicosFormatados = formatarServicosAtendidosParaExibir(usuario.servicos_atendidos);
      setEditedServicos(servicosFormatados);
    } else {
      setEditedServicos({
        atende: [],
        prioridades: {}
      });
    }
    
    setSearchServico('');
    setAlteracoesFeitas(false);
  };

  // Função para fechar o menu
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setAlteracoesFeitas(false);
  };

  // Manipular alteração do guichê
  const handleGuicheChange = (e) => {
    setTempGuiche(e.target.value);
    setAlteracoesFeitas(true);
  };

  // Manipular toggle de serviço no menu dropdown
  const handleToggleServicoInMenu = (id) => {
    setEditedServicos(prev => {
      // Verificar se o serviço já está atendido
      const isAtendido = prev.atende.includes(id);
      
      if (isAtendido) {
        // Remover o serviço
        const newAtende = prev.atende.filter(servicoId => servicoId !== id);
        
        // Remover a prioridade também
        const newPrioridades = { ...prev.prioridades };
        delete newPrioridades[id];
        
        return {
          atende: newAtende,
          prioridades: newPrioridades
        };
      } else {
        // Adicionar o serviço e definir prioridade padrão
        return {
          atende: [...prev.atende, id],
          prioridades: {
            ...prev.prioridades,
            [id]: 'normal'
          }
        };
      }
    });
    
    setAlteracoesFeitas(true);
  };

  // Manipular alteração de prioridade no menu dropdown
  const handlePrioridadeChangeInMenu = (id, prioridade) => {
    setEditedServicos(prev => ({
      ...prev,
      prioridades: {
        ...prev.prioridades,
        [id]: prioridade
      }
    }));
    
    setAlteracoesFeitas(true);
  };

  // Salvar alterações
  const handleSaveChanges = async () => {
    if (!selectedUsuario) return;
    
    try {
      // Salvar guichê se foi alterado
      const guicheResult = await atualizarGuicheUsuario(selectedUsuario.id, tempGuiche);
      
      // Formatar serviços para o backend antes de enviar
      const servicosFormatados = formatarServicosAtendidosParaSalvar(editedServicos);
      
      // Salvar serviços se foram alterados
      const servicosResult = await atualizarServicosUsuario(selectedUsuario.id, servicosFormatados);
      
      if (guicheResult.success && servicosResult.success) {
        // Atualizar dados locais do usuário para refletir as mudanças
        setSelectedUsuario(prevUser => ({
          ...prevUser,
          guiche: tempGuiche === '' ? null : tempGuiche,
          servicos_atendidos: editedServicos
        }));
        
        handleCloseMenu();
        
        // Mostrar mensagem de sucesso
        toast.success('Dados atualizados com sucesso! Os novos serviços já estão disponíveis.');
      } else {
        alert('Erro ao atualizar dados. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      alert('Erro ao atualizar dados. Por favor, tente novamente.');
    }
  };

  // Filtrar serviços com base na busca
  const filteredServicos = searchServico.trim() === ''
    ? servicosDisponiveis
    : servicosDisponiveis.filter(servico => (
      servico.nome.toLowerCase().includes(searchServico.toLowerCase()) ||
      servico.categoria?.toLowerCase().includes(searchServico.toLowerCase())
    ));

  // Função para solicitar confirmação de desativação
  const handleConfirmDesativar = (usuario) => {
    setUsuarioToDesativar(usuario);
    setConfirmDesativarOpen(true);
  };
  
  // Função para fechar o diálogo de confirmação
  const handleCloseConfirmDesativar = () => {
    setConfirmDesativarOpen(false);
    setUsuarioToDesativar(null);
  };
  
  // Função para confirmar a desativação
  const handleConfirmDesativarUsuario = () => {
    if (usuarioToDesativar) {
      // Chama a função onDelete, mas agora entendemos que ela vai desativar, não excluir
      onDelete(usuarioToDesativar.id);
      setConfirmDesativarOpen(false);
      setUsuarioToDesativar(null);
    }
  };

  return (
    <Box>
      {searchTerm && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {sortedAndFilteredUsuarios.length} {sortedAndFilteredUsuarios.length === 1 ? 'usuário encontrado' : 'usuários encontrados'}
          </Typography>
          {sortedAndFilteredUsuarios.length < usuarios.length && (
            <Button 
              size="small" 
              variant="text" 
              color="primary" 
              onClick={onClearSearch}
              sx={{ 
                fontSize: '0.75rem', 
                textTransform: 'none',
                p: 0,
                minWidth: 'auto'
              }}
            >
              Mostrar todos
            </Button>
          )}
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableCell 
                column="usuario" 
                label="Usuário" 
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
              />
              <SortableTableCell 
                column="nome" 
                label="Nome" 
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
              />
              <SortableTableCell 
                column="email" 
                label="Email" 
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
              />
              <SortableTableCell 
                column="cargo" 
                label="Cargo" 
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
              />
              <SortableTableCell 
                column="guiche" 
                label="Guichê" 
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
              />
              <SortableTableCell 
                column="ativo" 
                label="Status" 
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
              />
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAndFilteredUsuarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    color: 'text.secondary'
                  }}>
                    <SearchIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Nenhum usuário encontrado para "{searchTerm}"
                    </Typography>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      startIcon={<ClearIcon />} 
                      onClick={onClearSearch}
                      sx={{ mt: 1, borderRadius: '8px' }}
                    >
                      Limpar busca
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              sortedAndFilteredUsuarios.map((usuario) => (
                <TableRow 
                  key={usuario.id}
                  hover
                  sx={{ 
                    transition: 'all 0.3s',
                    ...(usuario.ativo 
                      ? {
                          '&:hover': {
                            backgroundColor: alpha(customTheme.palette.primary.main, 0.04)
                          }
                        } 
                      : {
                          opacity: 0.7,
                          filter: 'grayscale(100%)',
                          backgroundColor: theme => theme.palette.mode === 'dark' 
                            ? alpha(customTheme.palette.grey[900], 0.4) 
                            : alpha('#f5f5f5', 0.3),
                          borderLeft: `3px solid ${alpha(customTheme.palette.grey[500], 0.5)}`,
                          '&:hover': {
                            opacity: 0.85,
                            backgroundColor: theme => theme.palette.mode === 'dark'
                              ? alpha(customTheme.palette.grey[800], 0.3)
                              : alpha(customTheme.palette.grey[300], 0.2)
                          }
                        }
                    )
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserAvatar usuario={usuario} />
                      <span>{usuario.usuario}</span>
                    </Box>
                  </TableCell>
                  <TableCell>{usuario.nome}</TableCell>
                  <TableCell>{usuario.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatarCargo(usuario.cargo)}
                      color={getCargoColor(usuario.cargo)}
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        minWidth: 100,
                      }}
                    />
                  </TableCell>
                  <TableCell>{usuario.guiche || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={usuario.ativo ? 'Ativo' : 'Inativo'}
                      color={usuario.ativo ? 'success' : 'default'}
                      size="small"
                      variant={usuario.ativo ? 'filled' : 'outlined'}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Configurações rápidas">
                      <IconButton 
                        size="small"
                        onClick={(e) => handleSettingsClick(e, usuario)}
                        sx={{
                          color: customTheme.palette.info.main,
                          '&:hover': {
                            bgcolor: alpha(customTheme.palette.info.main, 0.1),
                          },
                          mr: 1
                        }}
                      >
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton 
                        size="small"
                        onClick={() => onEdit(usuario)}
                        sx={{
                          color: customTheme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(customTheme.palette.primary.main, 0.1),
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={usuario.ativo ? "Desativar" : "Reativar"}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleConfirmDesativar(usuario)}
                        sx={{
                          color: usuario.ativo 
                            ? customTheme.palette.error.main 
                            : customTheme.palette.success.main,
                          '&:hover': {
                            bgcolor: usuario.ativo
                              ? alpha(customTheme.palette.error.main, 0.1)
                              : alpha(customTheme.palette.success.main, 0.1),
                          }
                        }}
                      >
                        {usuario.ativo ? <DeleteIcon /> : <RefreshIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu de Configurações Rápidas */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: 320,
            maxWidth: 650,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
            mt: 1,
            pb: 2,
            maxHeight: '80vh',
            overflowY: 'auto'
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
            Configurações do Usuário
          </Typography>
          
          {/* Campo de guichê */}
          <TextField
            fullWidth
            label="Número do Guichê"
            type="number"
            size="small"
            value={tempGuiche}
            onChange={handleGuicheChange}
            margin="dense"
            variant="outlined"
            InputProps={{
              sx: { borderRadius: 1.5 }
            }}
            helperText="Deixe em branco para remover o guichê"
          />
        </Box>
        
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Serviços Atendidos
          </Typography>
          
          {/* Campo de busca de serviços */}
          <TextField
            fullWidth
            placeholder="Buscar serviço..."
            size="small"
            variant="outlined"
            value={searchServico}
            onChange={(e) => setSearchServico(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 1.5,
                fontSize: '0.875rem'
              }
            }}
          />
          
          {/* Lista de serviços */}
          <Box sx={{ 
            maxHeight: '300px',
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '10px',
            }
          }}>
            {carregandoServicos ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              filteredServicos.length === 0 ? (
                <Typography variant="caption" color="text.secondary" align="center" sx={{ py: 2, display: 'block' }}>
                  Nenhum serviço encontrado.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                  {filteredServicos.map((servico) => {
                    const isAtendido = editedServicos.atende.includes(servico.id);
                    
                    return (
                      <Box 
                        key={servico.id}
                        sx={{ 
                          p: 1,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: isAtendido
                            ? alpha(customTheme.palette.primary.main, 0.3)
                            : 'divider',
                          backgroundColor: isAtendido 
                            ? alpha(customTheme.palette.primary.main, 0.05)
                            : 'transparent',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%'
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={isAtendido}
                              onChange={() => handleToggleServicoInMenu(servico.id)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: isAtendido ? 600 : 400,
                                fontSize: '0.75rem',
                                lineHeight: 1.2,
                                wordBreak: 'break-word' 
                              }}
                            >
                              {servico.nome}
                            </Typography>
                          }
                          sx={{ 
                            m: 0,
                            alignItems: 'center',
                            '.MuiFormControlLabel-label': {
                              fontSize: '0.75rem'
                            }
                          }}
                        />
                        
                        {isAtendido && (
                          <Box sx={{ mt: 'auto', pt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Prioridade:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.2 }}>
                              {['baixa', 'normal', 'alta', 'urgente'].map(valor => {
                                const prioridade = editedServicos.prioridades[servico.id] || 'normal';
                                const colors = {
                                  'baixa': 'info',
                                  'normal': 'primary',
                                  'alta': 'warning',
                                  'urgente': 'error'
                                };
                                const labels = {
                                  'baixa': 'Baixa',
                                  'normal': 'Normal',
                                  'alta': 'Alta',
                                  'urgente': 'Urgente'
                                };
                                
                                return (
                                  <Chip
                                    key={valor}
                                    label={labels[valor]}
                                    size="small"
                                    color={prioridade === valor ? colors[valor] : 'default'}
                                    variant={prioridade === valor ? 'filled' : 'outlined'}
                                    onClick={() => handlePrioridadeChangeInMenu(servico.id, valor)}
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: '18px',
                                      cursor: 'pointer',
                                      fontWeight: prioridade === valor ? 'bold' : 'normal',
                                      '& .MuiChip-label': {
                                        px: 0.5
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )
            )}
          </Box>
        </Box>
        
        {/* Botões de ação */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 2, gap: 1 }}>
          <Button 
            variant="outlined" 
            color="inherit"
            size="small"
            onClick={handleCloseMenu}
            sx={{ borderRadius: 1.5 }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            size="small"
            onClick={handleSaveChanges}
            disabled={!alteracoesFeitas}
            sx={{ 
              borderRadius: 1.5,
              '&:not(:disabled)': {
                background: `linear-gradient(90deg, ${customTheme.palette.primary.main}, ${customTheme.palette.primary.dark})`,
              }
            }}
          >
            Salvar Alterações
          </Button>
        </Box>
      </Menu>
      
      {/* Diálogo de confirmação para desativar usuário */}
      <Dialog 
        open={confirmDesativarOpen} 
        onClose={handleCloseConfirmDesativar}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            maxWidth: '400px',
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2 }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            {usuarioToDesativar?.ativo 
              ? "Desativar Usuário" 
              : "Reativar Usuário"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <UserAvatar usuario={usuarioToDesativar || {}} />
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="body1" fontWeight="medium">
                {usuarioToDesativar?.nome}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                @{usuarioToDesativar?.usuario}
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body2">
            {usuarioToDesativar?.ativo 
              ? "Você está prestes a desativar este usuário. Usuários desativados não poderão fazer login no sistema nem atender senhas."
              : "Você está prestes a reativar este usuário. Ele poderá fazer login no sistema e atender senhas novamente."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseConfirmDesativar}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 1.5 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDesativarUsuario}
            variant="contained"
            color={usuarioToDesativar?.ativo ? "error" : "success"}
            startIcon={usuarioToDesativar?.ativo ? <DeleteIcon /> : <RefreshIcon />}
            sx={{ 
              borderRadius: 1.5,
              ml: 1,
              px: 2,
              ...(usuarioToDesativar?.ativo 
                ? {
                    bgcolor: customTheme.palette.error.main,
                    '&:hover': { bgcolor: customTheme.palette.error.dark }
                  }
                : {
                    bgcolor: customTheme.palette.success.main,
                    '&:hover': { bgcolor: customTheme.palette.success.dark }
                  }
              )
            }}
          >
            {usuarioToDesativar?.ativo 
              ? "Desativar Usuário"
              : "Reativar Usuário"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsuarioTable; 